import { useCallback, useEffect, useState } from "react";
import { getFeeDetail } from "../../../../services/StudentPortal/StudentFeeService";
import { useStudentSession } from "../../context/StudentSessionContext";
import { handleApiError } from "../../../../utils/errorHandler";

const pick = (obj, keys) => {
  for (const key of keys) {
    const value = obj?.[key];
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return undefined;
};

// Confirmed against a real populated response (2026-10-30, admission
// PP-8423): { data: { student_name, personal_details, enrollments,
// outstanding_bills: [], previous_bills: [{ bill_number, program_name,
// session, term, status, bill_type, paid_amount, receipt_link, due_date,
// paid_at }] } }. outstanding_bills was empty in that sample, so its amount
// field isn't confirmed — kept defensive (paid_amount/due_amount/amount)
// until a bill with actual dues is seen.
const normalizeBill = (bill) => ({
  billNumber: bill?.bill_number || null,
  programName: bill?.program_name || null,
  session: bill?.session || null,
  term: bill?.term || null,
  status: bill?.status || null,
  billType: bill?.bill_type || null,
  amount: Number(pick(bill, ["paid_amount", "due_amount", "amount"])) || 0,
  dueDate: bill?.due_date || null,
  paidAt: bill?.paid_at || null,
  receiptLink: bill?.receipt_link || null,
});

const normalizeFees = (raw) => {
  if (!raw) return null;
  const data = raw?.data && typeof raw.data === "object" ? raw.data : raw;

  const outstandingBills = Array.isArray(data.outstanding_bills)
    ? data.outstanding_bills.map(normalizeBill)
    : [];
  const previousBills = Array.isArray(data.previous_bills) ? data.previous_bills.map(normalizeBill) : [];

  const totalDue = outstandingBills.reduce((sum, bill) => sum + bill.amount, 0);
  const nextDueDate =
    outstandingBills
      .map((bill) => bill.dueDate)
      .filter(Boolean)
      .sort((a, b) => new Date(a) - new Date(b))[0] || null;

  return {
    studentName: data.student_name || null,
    totalDue,
    dueDate: nextDueDate,
    status: outstandingBills.length > 0 ? "Due" : "Paid",
    outstandingBills,
    previousBills,
  };
};

export const useStudentFees = () => {
  const { student } = useStudentSession();
  const admissionNumber = student?.id_number;
  const [fees, setFees] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchFees = useCallback(async () => {
    if (!admissionNumber) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const response = await getFeeDetail(admissionNumber);
      setFees(normalizeFees(response?.data));
    } catch (error) {
      // A 404 here just means "no fee record for this admission number" —
      // not worth a toast every time a student with no dues opens the page.
      if (error?.response?.status !== 404) {
        handleApiError(error);
      }
      setFees(null);
    } finally {
      setIsLoading(false);
    }
  }, [admissionNumber]);

  useEffect(() => {
    fetchFees();
  }, [fetchFees]);

  return { fees, isLoading };
};

export default useStudentFees;
